/**
 * Global event bus infrastrcture - a simple event emitter/observer
 * to dispatch global events.
 * 
 * Use rarely, if ever. Use parent/child communication instead, or use component states.
 * But if you really have to reach through the whole application, this is the way to go.
 */
type ObserverFn = (...args: any[]) => any;
type Event = string;

const observers: Map<Event, ObserverFn[] | null> = new Map();

/**
 * Register a callback function for an event
 *
 * @param event
 * @param callback
 */
export function on(event: Event, callback: any) {
    const observersForEvent = observers.get(event) || [];
    if (!observersForEvent.includes(callback)) {
        observersForEvent.push(callback);
    }
    observers.set(event, observersForEvent);
}

/**
 * Un-register a callback function for an event, or
 * un-register all callbacks for an event
 *
 * @param event
 * @param callback
 */
export function off(event: Event, callback: any = null) {
    if (callback === null) {
        observers.delete(event);
    } else {
        const observersForEvent = observers.get(event) || [];
        observersForEvent.splice(observersForEvent.indexOf(callback), 1);
        observers.set(event, observersForEvent);
    }
}

/**
 * Emit a global event to all registered listeners
 *
 * Note that there is no possibility to stop the chain of events:
 * An event is emitted to all listeners, independant of the return value of a single
 * event callback.
 *
 * @param event
 * @param args
 */
export function emit(event: Event, ...args: any[]) {
    const observersForEvent = observers.get(event) || [];
    observersForEvent.forEach((observerFn) => observerFn(...args));
}
